import * as React from "react";
import { Shield } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The escrow balance pinned to the foot of the sidebar.
 *
 * It is the one number that makes the guarantee feel real, so it is present on
 * every screen and it moves when money moves — a brand that verifies a
 * deliverable should watch this figure drop on the next render. That detail is
 * most of the reason the card exists.
 *
 * `fill` is the share of the bar that is drawn, 0–1. Pass a real ratio or leave
 * it at 1: a decorative fraction would be inventing a number on the one card
 * whose whole job is being trustworthy.
 */
export interface EscrowBalanceCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  /** Already formatted — `USD 4.150`. */
  amount: React.ReactNode;
  note?: React.ReactNode;
  fill?: number;
}

export function EscrowBalanceCard({
  label,
  amount,
  note,
  fill = 1,
  className,
  ...props
}: EscrowBalanceCardProps) {
  const pct = Math.max(0, Math.min(1, fill)) * 100;

  return (
    <div
      className={cn(
        "rounded-panel border border-neutral-200 bg-white p-3.5",
        className,
      )}
      {...props}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <Shield className="size-[13px] shrink-0 text-steel-600" strokeWidth={2.1} />
        <p className="adv-label">{label}</p>
      </div>

      <p className="text-figure-lg text-neutral-900">{amount}</p>

      <div className="mt-3 h-1 overflow-hidden rounded-sm bg-neutral-100">
        <div
          className="h-full origin-left animate-bar-grow bg-gradient-to-r from-steel-600 to-steel-400"
          style={{ width: `${pct}%`, animationDelay: "0.5s" }}
        />
      </div>

      {note && <p className="mt-2 text-micro text-neutral-500">{note}</p>}
    </div>
  );
}
